//React
import * as React from "react";
//Material UI
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import Tooltip from "@mui/material/Tooltip";
//Components
import { advisoriesFor, conflictFor, existingConflicts } from "./roleRules";

/**
 * The add/edit user dialog, built to NeuraDesigns `userport/index.html`.
 *
 * One component for both. The two were previously near-identical copies, which
 * is how the edit dialog ended up titled "Add User".
 *
 * MUI's `Dialog` is kept as the shell rather than hand-rolling the mockup's
 * `.modal` div: it brings the focus trap, Escape handling and the dialog role
 * that the mockup's markup lacks. Everything inside is the design's.
 *
 * The mockup shows User Groups and Regions as separate fields; the taxonomy
 * carries that split already, as the synthesised "IMS Regions" group.
 */

const REGION_GROUP_NAME = "IMS Regions";

const labelSx = {
  display: "block",
  fontSize: "0.74rem",
  color: "rgba(255, 255, 255, 0.6)",
  marginBottom: "6px",
  fontWeight: 500,
};

const inputSx = {
  width: "100%",
  padding: "10px 14px",
  background: "rgba(255, 255, 255, 0.04)",
  border: "1px solid rgba(255, 255, 255, 0.08)",
  color: "var(--text)",
  borderRadius: "8px",
  fontSize: "0.88rem",
  fontFamily: "inherit",
  outline: "none",
  "&::placeholder": { color: "rgba(255, 255, 255, 0.25)" },
  "&:focus": {
    borderColor: "rgba(253, 185, 64, 0.45)",
    boxShadow: "0 0 0 3px rgba(253, 185, 64, 0.10)",
  },
};

const checkRowSx = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontSize: "0.84rem",
  color: "rgba(255, 255, 255, 0.85)",
  cursor: "pointer",
  "& input": { width: 16, height: 16, accentColor: "var(--accent)", cursor: "pointer" },
};

const checkGridSx = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
  gap: "8px 12px",
};

function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

function Field({ id, label, type = "text", value, placeholder, onChange, required }) {
  return (
    <Box sx={{ marginBottom: "14px" }}>
      <Box component="label" htmlFor={id} sx={labelSx}>
        {label}
      </Box>
      <Box
        component="input"
        id={id}
        name={id}
        type={type}
        required={required}
        placeholder={placeholder}
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        sx={inputSx}
      />
    </Box>
  );
}

function Checks({ entries, selected, onToggle, labelFor }) {
  return (
    <Box sx={checkGridSx}>
      {entries.map((entry) => {
        const checked = selected.includes(entry.unique_name);
        // Only an UNCHECKED box is ever disabled. A user who already holds a
        // conflicting pair — legacy data, or a rule added after the fact — must
        // still be able to untick one to resolve it.
        const conflict = checked ? null : conflictFor(entry.unique_name, selected);

        const row = (
          <Box
            component="label"
            sx={{
              ...checkRowSx,
              ...(conflict ? { opacity: 0.4, cursor: "not-allowed" } : {}),
            }}
          >
            <input
              type="checkbox"
              name={entry.unique_name}
              checked={checked}
              disabled={Boolean(conflict)}
              onChange={(event) => onToggle(entry.unique_name, event.target.checked)}
            />
            {entry.human_readable_name}
          </Box>
        );

        if (!conflict) return <React.Fragment key={entry.unique_name}>{row}</React.Fragment>;

        return (
          <Tooltip
            key={entry.unique_name}
            placement="top"
            title={`Can't be combined with ${labelFor(conflict.conflictsWith)} — ${conflict.reason}`}
          >
            {/* A disabled input swallows pointer events, so the tooltip needs a
                wrapper that still receives them. */}
            <span style={{ display: "inline-flex" }}>{row}</span>
          </Tooltip>
        );
      })}
    </Box>
  );
}

function Advisory({ level, children }) {
  const warning = level === "warning";
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: "8px",
        padding: "8px 12px",
        borderRadius: "var(--radius-md)",
        fontSize: "0.78rem",
        lineHeight: 1.5,
        marginBottom: "8px",
        background: warning ? "var(--accent-tint)" : "rgba(255, 255, 255, 0.04)",
        border: `1px solid ${warning ? "rgba(253, 185, 64, 0.22)" : "var(--border)"}`,
        color: warning ? "var(--accent)" : "var(--text-secondary)",
      }}
    >
      <Box component="span" aria-hidden="true" sx={{ flexShrink: 0, marginTop: "1px" }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      </Box>
      {children}
    </Box>
  );
}

export default function UserDialog({
  open,
  title,
  idPrefix,
  values,
  onChange,
  availableGroups,
  selectedGroups,
  onToggleGroup,
  onClose,
  onSubmit,
  actions,
}) {
  const groups = availableGroups || [];
  const roleGroups = groups.filter((group) => group.group_name !== REGION_GROUP_NAME);
  const regionGroup = groups.find((group) => group.group_name === REGION_GROUP_NAME);

  // Resolve a unique_name to its display label, falling back to the raw name so
  // a group the taxonomy has not caught up with still reads sensibly.
  const labelFor = (unique_name) => {
    for (const group of groups) {
      const match = (group.user_groups || []).find((entry) => entry.unique_name === unique_name);
      if (match) return match.human_readable_name;
    }
    return unique_name;
  };

  const advisories = advisoriesFor(selectedGroups);
  const alreadyConflicting = existingConflicts(selectedGroups);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      aria-labelledby={`${idPrefix}-title`}
      BackdropProps={{
        // The dialogs previously passed `invisible: true`, so they floated
        // with no scrim at all. The design dims and blurs behind them.
        sx: { backgroundColor: "rgba(0, 0, 0, 0.7)", backdropFilter: "blur(4px)" },
      }}
      PaperProps={{ sx: { maxWidth: "520px", maxHeight: "90vh" } }}
    >
      <Box component="form" onSubmit={onSubmit} sx={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Box
          sx={{
            padding: "18px 22px",
            borderBottom: "1px solid var(--border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
          }}
        >
          <Box component="span" id={`${idPrefix}-title`} sx={{ fontSize: "1.04rem", fontWeight: 600 }}>
            {title}
          </Box>
          <Box
            component="button"
            type="button"
            onClick={onClose}
            aria-label="Close"
            sx={{
              width: 28,
              height: 28,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: "rgba(255, 255, 255, 0.6)",
              cursor: "pointer",
              borderRadius: "6px",
              border: "none",
              background: "transparent",
              "&:hover": { background: "rgba(255, 255, 255, 0.06)", color: "var(--text)" },
            }}
          >
            <CloseIcon />
          </Box>
        </Box>

        <Box sx={{ padding: "20px 22px", overflowY: "auto", flex: 1 }}>
          <Field
            id={`${idPrefix}-email`}
            label="Email"
            type="email"
            required
            placeholder="name@cauchyconsult.com"
            value={values.email}
            onChange={(value) => onChange("email", value)}
          />
          <Field
            id={`${idPrefix}-first`}
            label="First Name"
            required
            value={values.first_name}
            onChange={(value) => onChange("first_name", value)}
          />
          <Field
            id={`${idPrefix}-last`}
            label="Last Name"
            required
            value={values.last_name}
            onChange={(value) => onChange("last_name", value)}
          />

          <Box sx={{ marginBottom: "14px" }}>
            <Box component="span" sx={labelSx}>
              User Groups
            </Box>
            {roleGroups.map((group) => (
              <Box key={group.group_name} sx={{ marginBottom: "16px", "&:last-of-type": { marginBottom: 0 } }}>
                <Box
                  sx={{
                    fontSize: "0.72rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    color: "rgba(255, 255, 255, 0.4)",
                    fontWeight: 600,
                    marginBottom: "8px",
                  }}
                >
                  {group.group_name}
                </Box>
                <Checks
                  entries={group.user_groups}
                  selected={selectedGroups}
                  onToggle={onToggleGroup}
                  labelFor={labelFor}
                />
              </Box>
            ))}
          </Box>

          {/* A pair that is already saved cannot be blocked retrospectively, so
              say so plainly rather than leaving it silently invalid. */}
          {alreadyConflicting.map((rule) => (
            <Advisory key={rule.pair.join("+")} level="warning">
              <span>
                <strong>
                  {labelFor(rule.pair[0])} and {labelFor(rule.pair[1])} conflict.
                </strong>{" "}
                {rule.reason} Untick one to resolve it.
              </span>
            </Advisory>
          ))}

          {advisories.map((note) => (
            <Advisory key={note.id} level={note.level}>
              <span>{note.text}</span>
            </Advisory>
          ))}

          {regionGroup && regionGroup.user_groups.length ? (
            <Box sx={{ marginBottom: "14px" }}>
              <Box component="span" sx={labelSx}>
                Regions
              </Box>
              <Checks
                entries={regionGroup.user_groups}
                selected={selectedGroups}
                onToggle={onToggleGroup}
                labelFor={labelFor}
              />
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            padding: "14px 22px 18px",
            display: "flex",
            gap: "10px",
            justifyContent: "flex-end",
            borderTop: "1px solid rgba(255, 255, 255, 0.04)",
            flexWrap: "wrap",
          }}
        >
          {actions}
        </Box>
      </Box>
    </Dialog>
  );
}
